import styled, { useTheme } from 'styled-components';
import { useRef, useEffect } from 'react';
import React from 'react';
import {
  BodyMedium,
  BodySmall,
  CaptionSmall,
  ParagraphSmallText,
} from '../../Texts';
import { useSelector } from 'react-redux';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import {
  useGetCurrentUserProfileQuery,
  useLazyGetCurrentUserProfileQuery,
} from '../../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../../redux/slices/auth';
import {
  useGetNotificationsQuery,
  useReadNotificationsMutation,
} from '../../../redux/api/authorized/notifications';
import { useNavigate } from 'react-router-dom';
import { hapticsImpactLight } from '../../../common/haptic';
import { menuController } from '@ionic/core/components';
import NotificationIcon from '../../../assets/icons/notification.svg?react';
import NotificationNewIcon from '../../../assets/icons/notification-new.svg?react';

const Container = styled.div`
  position: relative;
  display: inline-block;
  @media ${PHONE_MEDIA_QUERY} {
    position: unset;
  }
`;

const Content = styled.div`
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 300px;
  padding: 16px 0;
  background: ${({ theme }) => theme.background.primary};
  border-radius: 16px;
  gap: 8px;
  flex-direction: column;
  padding: 16px;
  box-shadow:
    0px 8px 20px 0px ${({ theme }) => theme.text.title}14,
    0px 12px 32px 4px ${({ theme }) => theme.text.title}0a;
  z-index: 1000;
  @media ${PHONE_MEDIA_QUERY} {
    border-top-right-radius: 0px;
    border-top-left-radius: 0px;
    top: calc(100% - 1px);
    left: -16px;
    box-shadow: unset;
    width: 100vw;
  }
`;

const TriggerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  cursor: pointer;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 8px;
  }
`;

const NotificationContainer = styled.div`
  display: flex;
  border-radius: 8px;
  gap: 8px;
  color: ${({ theme }) => theme.text.regular};
  cursor: pointer;
  position: relative;
`;

const ScrollContainer = styled.div`
  overflow-y: scroll;
  max-height: 425px;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const NotificationImage = styled.img`
  width: 32px;
  height: 32px;
  ${({ disabled }) => disabled && 'filter: grayscale(100%);'}
`;

const NotificationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 20px;
  }
`;

const Line = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.border.dashed};
  width: 100%;
`;

const DateContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  align-items: center;
`;

export const NotificationsContent = ({ setIsOpen }) => {
  const privyToken = useSelector(selectPrivyToken);
  const { data: notifications } = useGetNotificationsQuery(undefined, {
    skip: !privyToken,
  });

  const navigate = useNavigate();

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();

  return (
    <>
      <NotificationsContainer>
        {notifications?.map((notification, index) => (
          <>
            <NotificationContainer
              onClick={() => {
                hapticsImpactLight();
                if (notification.onClickRedirect) {
                  if (notification.onClickRedirect[0] === '/') {
                    // If onClickRedirect is a relative path (inside deks), navigate to it
                    navigate(notification.onClickRedirect);
                  } else {
                    // If onClickRedirect is an absolute path, open window
                    window.open(notification.onClickRedirect);
                  }
                }
                if (isMobile) {
                  menuController.toggle('notifications');
                } else {
                  if (setIsOpen) {
                    setIsOpen(false);
                  }
                }
              }}
            >
              <NotificationImage src={notification.imageUrl} />
              <div>
                <BodyMedium>
                  <span style={{ fontWeight: 600, color: theme.text.title }}>
                    {notification.title}
                  </span>
                </BodyMedium>
                <BodySmall>{notification.body}</BodySmall>
              </div>
              <DateContainer>
                <CaptionSmall>
                  {new Date(notification.createdDate).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                    },
                  )}
                </CaptionSmall>
              </DateContainer>
            </NotificationContainer>
            {index !== notifications.length - 1 && <Line />}
          </>
        ))}
        {notifications?.length === 0 && (
          <BodySmall style={isMobile ? { textAlign: 'center' } : {}}>
            You don't have notifications yet
          </BodySmall>
        )}
      </NotificationsContainer>
    </>
  );
};

const Notifications = ({ isOpen, setIsOpen }) => {
  const modalRef = useRef(null);
  const privyToken = useSelector(selectPrivyToken);
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const theme = useTheme();

  const [triggerGetUser] = useLazyGetCurrentUserProfileQuery();

  const navigate = useNavigate();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const handleClickOutside = (event) => {
    if (
      modalRef.current &&
      !modalRef.current.contains(event.target) &&
      setIsOpen
    ) {
      setIsOpen(false);
    }
  };

  const [readNotification] = useReadNotificationsMutation();

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    hapticsImpactLight();
  }, [isOpen]);

  return (
    <>
      <Container ref={modalRef}>
        <TriggerContainer
          onClick={async () => {
            if (isMobile) {
              hapticsImpactLight();
              menuController.toggle('notifications');
              readNotification().then(() => {
                triggerGetUser();
              });
            } else {
              if (!isOpen) {
                readNotification().then(() => {
                  triggerGetUser();
                });
              }
              setIsOpen(!isOpen);
            }
          }}
        >
          {!currentUser?.pendingNotification ? (
            <NotificationIcon
              width={24}
              height={24}
              style={{ color: theme.text.title }}
            />
          ) : (
            <NotificationNewIcon
              width={24}
              height={24}
              style={{ color: theme.text.title }}
            />
          )}
        </TriggerContainer>
        <Content isOpen={isOpen}>
          <ParagraphSmallText
            style={{ fontWeight: 600, color: theme.text.title }}
          >
            Notifications
          </ParagraphSmallText>
          <ScrollContainer>
            <NotificationsContent setIsOpen={setIsOpen} />
          </ScrollContainer>
        </Content>
      </Container>
    </>
  );
};

export default Notifications;
